// src/redux/fleetSlice.ts
// ─────────────────────────────────────────────────────────────────────────────
// Mirrors shortstaysSlice.ts pattern exactly: hostId only (no driverId),
// brandId auto-detected from commercePartners, Firestore collection "Fleet".
// ─────────────────────────────────────────────────────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc, getDoc, deleteDoc, collection, addDoc,
  query, where, onSnapshot, serverTimestamp,
  orderBy, limit, getDocs, updateDoc, writeBatch,
} from "firebase/firestore";
import { ref as dbRef, onValue, off } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { db, rtdb, storage } from "../firebase";
import type { AppDispatch, RootState } from "./store";

/* ─────────────────────────────────────────────────────────────────────────── */
/* TYPES                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

export interface DriverProfile {
  listingId:  string;   // random Firestore doc ID
  hostId:     string;   // current user UID
  brandId?:   string;   // auto-detected commerce partner brand (categoryId 2, subcategoryId 201)
  rideType?:     number | null;
  vehicleType?:  number | null;
  vehicleSize?:  string;
  businessType?: number | null;
  brand?:        number | null;
  classType?:    number | null;
  carSize?:      number | null;
  color?:        number | null;
  transmission?: number | null;
  fuelType?:     number | null;
  model?:        string;
  licensePlate?: string;
  year?:         string;
  capacity?:     string;
  description?:  string;
  photos?:       string[];
  rate?:            number;
  rateType?:        "perSurge" | "perPerson" | "perPersonPerKm" | "perKm" | "perDay" | "perWeek"
                   | "perMonth" | "perYear" | "perCar" | "perSeat" | "perMinute" | "perHour";
  extras?:          Record<string, number>;
  extraCharges?:    number;
  discountApplied?: Record<string, boolean>;
  rideSharing?: boolean;
  city?:    string;
  street?:  string;
  country?: string;
  isOnline?:        boolean;
  isBusy?:          boolean;
  isAvailable?:     boolean;
  serviceRadiusKm?: number;
  operationHours?:  string;
  allowParcel?: boolean;
  // Uber-Fleet-style "which vehicle am I driving right now" flag. Only one
  // listing per hostId should be true at any given time.
  isSelected?:  boolean;
  driverId?:    string | null; // userId of whoever currently has this vehicle active (may be the hostId themself, or any other driver)
  isPublished:  boolean;
  isFlagged:    boolean;
  isComplete:   boolean;
  isReviewed:   boolean;
  inReview:     boolean;
  isDraft:      boolean;
  isPromoted?:  boolean;
  insured?:     boolean;
  agreePolicy?: boolean;
  progress?:    string;
  createdAt?: number | null;
  updatedAt?: number | null;
  [key: string]: any;
}

export interface ActiveTrip {
  tripId:         string;
  riderId:        string;
  driverId:       string | null;
  listingId:      string | null;
  status:         string;
  pickupLat:      number;
  pickupLng:      number;
  dropoffLat:     number | null;
  dropoffLng:     number | null;
  pickupAddress:  string;
  dropoffAddress: string;
  distanceKm:     number;
  rateType:       string;
  totalCharged:   number;
  guestFee:       number;
  hostFee:        number;
  driverProfit:   number;
  platformProfit: number;
  isPaid:         boolean;
  isConfirmed:    boolean;
  isCancelled:    boolean;
  etaMinutes?:    number;
}

export interface ActiveParcel {
  parcelId:        string;
  status:          string;
  pickup_address:  string;
  dropoff_address: string;
  distanceKm:      number;
  courierPayout:   number;
  is_fragile:      boolean;
  is_urgent:       boolean;
  weight_kg:       number;
  etaMinutes?:     number;
}

interface FleetState {
  currentDriver:        DriverProfile | null;
  allDrivers:           DriverProfile[];
  listings:             DriverProfile[];
  activeTrip:           ActiveTrip | null;
  activeParcel:         ActiveParcel | null;
  pendingTripRequest:   any | null;
  pendingParcelRequest: any | null;
  unsubscribe?: () => void;
  loading:              boolean;
  error:                string | null;
}

const initialState: FleetState = {
  currentDriver:        null,
  allDrivers:           [],
  listings:             [],
  activeTrip:           null,
  activeParcel:         null,
  pendingTripRequest:   null,
  pendingParcelRequest: null,
  unsubscribe:          undefined,
  loading:              false,
  error:                null,
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* HELPERS                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

const toMillis = (val: any): number | null => {
  if (!val) return null;
  if (typeof val?.toMillis === "function") return val.toMillis();
  if (val?._seconds !== undefined) return val._seconds * 1000 + (val._nanoseconds ?? 0) / 1e6;
  if (typeof val === "number") return val;
  return null;
};

const parseDriver = (docId: string, raw: any): DriverProfile => ({
  listingId: raw.listingId ?? docId,
  hostId:    raw.hostId    ?? "",
  brandId:   raw.brandId   ?? undefined,
  rideType:     raw.rideType     ?? null,
  vehicleSize:  raw.vehicleSize  ?? null,
  vehicleType:  raw.vehicleType  ?? null,
  brand:        raw.brand        ?? null,
  classType:    raw.classType    ?? null,
  carSize:      raw.carSize      ?? null,
  color:        raw.color        ?? null,
  transmission: raw.transmission ?? null,
  fuelType:     raw.fuelType     ?? null,
  model:        raw.model        ?? "",
  licensePlate: raw.licensePlate ?? "",
  year:         raw.year         ?? "",
  capacity:     raw.capacity     ?? "",
  description:  raw.description  ?? "",
  photos:       raw.photos       ?? [],
  rate:            raw.rate            ?? null,
  rateType:        raw.rateType        ?? "perKm",
  extras:          raw.extras          ?? {},
  extraCharges:    raw.extraCharges    ?? 0,
  discountApplied: raw.discountApplied ?? {},
  businessType: raw.businessTypeId ?? raw.businessType ?? null,
  rideSharing:  raw.rideSharing    ?? false,
  city:    raw.address?.city    ?? raw.city    ?? "",
  street:  raw.address?.street  ?? raw.street  ?? "",
  country: raw.address?.country ?? raw.country ?? "",
  isOnline:        raw.isOnline        ?? false,
  isBusy:          raw.isBusy          ?? false,
  isAvailable:     raw.isAvailable     ?? false,
  serviceRadiusKm: raw.serviceRadiusKm ?? 0,
  operationHours:  raw.operationHours  ?? "",
  allowParcel:     raw.allowParcel     ?? false,
  insured:         raw.insured         ?? false,
  agreePolicy:     raw.agreePolicy     ?? false,
  progress:        raw.progress        ?? "",
  isSelected:  raw.isSelected  ?? false,
  driverId:    raw.driverId    ?? null,
  isPublished: raw.isPublished ?? false,
  isFlagged:   raw.isFlagged   ?? false,
  isComplete:  raw.isComplete  ?? false,
  isReviewed:  raw.isReviewed  ?? false,
  inReview:    raw.inReview    ?? false,
  isDraft:     raw.isDraft     ?? true,
  isPromoted:  raw.isPromoted  ?? false,
  createdAt: toMillis(raw.createdAt),
  updatedAt: toMillis(raw.updatedAt),
});

const toActiveTrip = (data: any): ActiveTrip => ({ ...data } as ActiveTrip);

/* ─────────────────────────────────────────────────────────────────────────── */
/* THUNKS                                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * upsertDriver
 *
 * Mirrors upsertShortstay exactly:
 *  - Auto-detects brandId from commercePartners (categoryId 2, subcategoryId 201)
 *    for the current user. brandId is optional — only attached if found.
 *  - hostId is always the authenticated user's UID.
 *  - CREATE: no listingId → addDoc generates random ID, listingId written back.
 *  - UPDATE: listingId supplied → updateDoc on that doc.
 *  - First vehicle a host ever creates is automatically isSelected: true.
 */
export const upsertDriver = createAsyncThunk<
  DriverProfile,
  { listingId?: string; data: Partial<DriverProfile> },
  { rejectValue: string }
>(
  "fleet/upsertDriver",
  async ({ listingId, data }, { rejectWithValue }) => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");

      // ⭐ TRY to find brandId — but don't block if not found
      const partnersQuery = query(
        collection(db, "commercePartners"),
        where("userId", "==", uid),
        where("categoryId", "==", 2),
        where("subcategoryId", "==", 201)
      );
      const partnersSnap = await getDocs(partnersQuery);
      const brandId = !partnersSnap.empty
        ? (partnersSnap.docs[0].data()?.brandId ?? partnersSnap.docs[0].id)
        : null;

      const baseData: any = {
        ...data,
        ...(brandId && { brandId }),
        categoryId: 2,
        subcategoryId: 201,
        hostId: uid,
      };

      // ── UPDATE EXISTING ──────────────────────────────────────────────────
      if (listingId) {
        const docRef = doc(db, "Fleet", listingId);
        await updateDoc(docRef, {
          ...baseData,
          listingId,
          updatedAt: serverTimestamp(),
        });
        const snap = await getDoc(docRef);
        return parseDriver(snap.id, snap.data());
      }

      // ── CREATE NEW ───────────────────────────────────────────────────────
      const existingSnap = await getDocs(
        query(collection(db, "Fleet"), where("hostId", "==", uid))
      );
      const isFirstVehicle = existingSnap.empty;

      const newDocRef = await addDoc(collection(db, "Fleet"), {
        ...baseData,
        isSelected:  isFirstVehicle, // first vehicle ever added becomes active
        driverId:    isFirstVehicle ? uid : null, // host becomes the driver of record by default
        isPublished: false,
        isFlagged:   false,
        isComplete:  false,
        isReviewed:  false,
        inReview:    false,
        isDraft:     true,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });

      // Write listingId back as a field — mirrors shortstay pattern exactly
      await updateDoc(newDocRef, { listingId: newDocRef.id });

      const snap = await getDoc(newDocRef);
      return parseDriver(snap.id, snap.data());
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to upsert driver");
    }
  }
);

/**
 * upsertDriverPhotos
 * Takes listingId (random doc ID) — mirrors upsertShortstayPhoto.
 */
export const upsertDriverPhotos = createAsyncThunk<
  string[],
  { listingId: string; photos: File[] },
  { state: RootState; rejectValue: string }
>(
  "fleet/upsertDriverPhotos",
  async ({ listingId, photos }, { getState, rejectWithValue }) => {
    try {
      const uid = getAuth().currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");

      const uploaded: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        const file = photos[i];
        const name = file.name || `photo-${Date.now()}-${i}`;
        const sRef = storageRef(storage, `Fleet/${listingId}/photos/${name}`);
        await uploadBytes(sRef, file);
        uploaded.push(await getDownloadURL(sRef));
      }

      const existing = getState().fleet.currentDriver?.photos ?? [];
      const merged   = [...existing, ...uploaded];

      await updateDoc(doc(db, "Fleet", listingId), {
        photos:    merged,
        updatedAt: serverTimestamp(),
      });

      return merged;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/**
 * deleteDriver — deletes listing by listingId (random doc ID).
 * Mirrors deleteShortstay.
 */
export const deleteDriver = createAsyncThunk(
  "fleet/deleteDriver",
  async (listingId: string, { rejectWithValue }) => {
    try {
      const snap = await getDoc(doc(db, "Fleet", listingId));
      if (!snap.exists()) throw new Error("Listing not found");
      for (const url of (snap.data()?.photos ?? []) as string[]) {
        try { await deleteObject(storageRef(storage, url)); } catch {}
      }
      await deleteDoc(doc(db, "Fleet", listingId));
      return listingId;
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/**
 * setActiveVehicle
 *
 * Uber-Fleet-style "which vehicle am I driving right now" switch. Marks
 * `listingId` as isSelected: true and every other listing owned by
 * `hostId` as isSelected: false, in a single atomic batch write.
 */


/**
 * setActiveVehicle
 *
 * Toggle: tapping a listing that's already selected BY THIS DRIVER deselects
 * it. Tapping any other listing selects it.
 *
 * Scope: a single Fleet brand/hostId can have MULTIPLE drivers, each with
 * their own active vehicle at the same time (e.g. hostId assigns cars to
 * several drivers). So when selecting a new vehicle we only clear out THIS
 * driver's own previous pick (selectedBy === userId) — we never touch
 * listings selectedBy a different driver, even under the same hostId.
 * That means this writes at most 2 docs (old pick + new pick), never the
 * whole fleet.
 */
export const setActiveVehicle = createAsyncThunk<
  { hostId: string; listingId: string; userId: string; isSelected: boolean },
  { hostId: string; listingId: string; userId: string },
  { rejectValue: string }
>(
  "fleet/setActiveVehicle",
  async ({ hostId, listingId, userId }, { rejectWithValue }) => {
    try {
      const targetRef  = doc(db, "Fleet", listingId);
      const targetSnap = await getDoc(targetRef);
      if (!targetSnap.exists()) throw new Error("Listing not found");
      const targetData = targetSnap.data();

      const isCurrentlyMine = targetData.isSelected === true && targetData.driverId === userId;

      // ── TAP TO DESELECT ────────────────────────────────────────────────
      if (isCurrentlyMine) {
        await updateDoc(targetRef, {
          isSelected: false,
          driverId:   null,
          updatedAt:  serverTimestamp(),
        });
        return { hostId, listingId, userId, isSelected: false };
      }

      // ── TAP TO SELECT ───────────────────────────────────────────────────
      // Only find MY previous active listing under this host — other
      // drivers' active vehicles (different driverId) are left alone.
      const prevSnap = await getDocs(
        query(
          collection(db, "Fleet"),
          where("hostId", "==", hostId),
          where("driverId", "==", userId),
          where("isSelected", "==", true)
        )
      );

      const batch = writeBatch(db);
      prevSnap.docs.forEach((d) => {
        if (d.id !== listingId) {
          batch.update(d.ref, { isSelected: false, driverId: null, updatedAt: serverTimestamp() });
        }
      });
      batch.update(targetRef, { isSelected: true, driverId: userId, updatedAt: serverTimestamp() });
      await batch.commit();

      return { hostId, listingId, userId, isSelected: true };
    } catch (e: any) {
      return rejectWithValue(e.message ?? "Failed to set active vehicle");
    }
  }
);

export const fetchUserTrips = createAsyncThunk<
  ActiveTrip[],
  { userId: string; role?: "rider" | "driver" }
>(
  "fleet/fetchUserTrips",
  async ({ userId, role = "rider" }) => {
    const field = role === "driver" ? "hostId" : "riderId";
    const snap  = await getDocs(
      query(
        collection(db, "rideTrips"),
        where(field, "==", userId),
        orderBy("createdAt", "desc"),
        limit(20)
      )
    );
    return snap.docs.map((d) => toActiveTrip(d.data()));
  }
);

export const fetchOperatorsByUserId = createAsyncThunk<any[], string, { rejectValue: string }>(
  "fleet/fetchOperatorsByUserId",
  async (hostId, { rejectWithValue }) => {
    try {
      const snap = await getDocs(
        query(collection(db, "operators"), where("hostId", "==", hostId))
      );
      return snap.docs.map((d) => ({ operatorId: d.id, ...d.data() }));
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

/* ─────────────────────────────────────────────────────────────────────────── */
/* LISTENERS                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * listenToDriverData
 * Queries by hostId field (= user UID). Real-time — this is the source of
 * truth `DriverCars` should read `listings` from.
 */
export const listenToDriverData =
  (hostId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!hostId) return;
    const q = query(collection(db, "Fleet"), where("hostId", "==", hostId));
    return onSnapshot(
      q,
      (snap) => {
        const listings = snap.docs.map((d) => parseDriver(d.id, d.data()));
        dispatch(setListings(listings));
        dispatch(setCurrentDriver(listings.find((l) => l.isSelected) ?? listings[0] ?? null));
      },
      (e) => dispatch(setError(e.message))
    );
  };

/**
 * listenToHostFleet
 * Direct equivalent of listenToHostShortstays — self-authenticates, cleans up
 * any prior listener, and filters strictly to the current user's hostId.
 */
export const listenToHostFleet =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const user = getAuth().currentUser;
    if (!user || !user.uid) {
      console.warn("⚠️ listenToHostFleet skipped — user not authenticated");
      dispatch(setListings([]));
      return;
    }

    const hostId = user.uid;

    const existingUnsub = getState().fleet.unsubscribe;
    if (existingUnsub) {
      existingUnsub();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(collection(db, "Fleet"), where("hostId", "==", hostId));

    const unsubscribeFn = onSnapshot(
      q,
      (snap) => {
        const listings = snap.docs
          .map((d) => parseDriver(d.id, d.data()))
          .filter((l) => l.hostId === hostId);
        dispatch(setListings(listings));
        dispatch(setCurrentDriver(listings.find((l) => l.isSelected) ?? listings[0] ?? null));
      },
      (e) => dispatch(setError(e.message))
    );

    dispatch(setUnsubscribe(unsubscribeFn));
  };

export const listenToAllDrivers =
  (brandId?: string) =>
  (dispatch: AppDispatch): (() => void) => {
    const constraints: any[] = [where("isPublished", "==", true)];
    if (brandId) constraints.push(where("brandId", "==", brandId));
    const q = query(collection(db, "Fleet"), ...constraints);
    return onSnapshot(
      q,
      (snap) => dispatch(setAllDrivers(snap.docs.map((d) => parseDriver(d.id, d.data())))),
      (e)    => dispatch(setError(e.message))
    );
  };

export const listenToDriverTripRequests =
  (hostId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!hostId) return;
    const r = dbRef(rtdb, `driverRequests/${hostId}`);
    const handler = onValue(r, (snap) => {
      snap.forEach((child) => {
        const val = child.val();
        if (val) dispatch(setPendingTripRequest({ ...val, tripKey: child.key }));
      });
    });
    return () => off(r, "value", handler);
  };

export const listenToDriverParcelRequests =
  (hostId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!hostId) return;
    const r = dbRef(rtdb, `driverParcelRequests/${hostId}`);
    const handler = onValue(r, (snap) => {
      snap.forEach((child) => {
        const val = child.val();
        if (val) dispatch(setPendingParcelRequest({ ...val, parcelKey: child.key }));
      });
    });
    return () => off(r, "value", handler);
  };

export const listenToActiveTrip =
  (tripId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!tripId) return;
    return onSnapshot(
      doc(db, "rideTrips", tripId),
      (snap) => {
        if (snap.exists()) dispatch(setActiveTrip(toActiveTrip(snap.data())));
        else               dispatch(setActiveTrip(null));
      },
      (e) => dispatch(setError(e.message))
    );
  };

export const listenToRiderTripStatus =
  (riderId: string, tripId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!riderId || !tripId) return;
    const r = dbRef(rtdb, `riderTrips/${riderId}/${tripId}`);
    const handler = onValue(
      r,
      (snap) => { const val = snap.val(); if (val) dispatch(updateActiveTripFields(val)); },
      (e)    => dispatch(setError(e.message))
    );
    return () => off(r, "value", handler);
  };

export const listenToOperator =
  (brandId: string) =>
  (dispatch: AppDispatch): (() => void) | undefined => {
    if (!brandId) return;
    return onSnapshot(doc(db, "operators", brandId), (snap) => {
      if (snap.exists()) console.log("[listenToOperator] updated:", brandId);
    });
  };

/* ─────────────────────────────────────────────────────────────────────────── */
/* SLICE                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

const fleetSlice = createSlice({
  name: "fleet",
  initialState,
  reducers: {
    setCurrentDriver: (state, action: PayloadAction<DriverProfile | null>) => {
      state.currentDriver = action.payload;
    },
    setAllDrivers: (state, action: PayloadAction<DriverProfile[]>) => {
      state.allDrivers = action.payload;
    },
    setListings: (state, action: PayloadAction<DriverProfile[]>) => {
      state.listings = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setActiveTrip: (state, action: PayloadAction<any>) => {
      state.activeTrip = action.payload as ActiveTrip | null;
    },
    updateActiveTripFields: (state, action: PayloadAction<Partial<ActiveTrip>>) => {
      if (state.activeTrip) state.activeTrip = { ...state.activeTrip, ...action.payload };
    },
    setActiveParcel: (state, action: PayloadAction<ActiveParcel | null>) => {
      state.activeParcel = action.payload;
    },
    setPendingTripRequest:     (state, action: PayloadAction<any>) => { state.pendingTripRequest    = action.payload; },
    clearPendingTripRequest:   (state)                             => { state.pendingTripRequest    = null; },
    setPendingParcelRequest:   (state, action: PayloadAction<any>) => { state.pendingParcelRequest  = action.payload; },
    clearPendingParcelRequest: (state)                             => { state.pendingParcelRequest  = null; },
    setError:   (state, action: PayloadAction<string>) => { state.error = action.payload; state.loading = false; },
    setLoading: (state, action: PayloadAction<boolean>) => { state.loading = action.payload; },
    clearActiveTrip: (state) => { state.activeTrip = null; },
    clearAll: (state) => {
      state.currentDriver        = null;
      state.allDrivers           = [];
      state.listings             = [];
      state.activeTrip           = null;
      state.activeParcel         = null;
      state.pendingTripRequest   = null;
      state.pendingParcelRequest = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(upsertDriver.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(upsertDriver.fulfilled, (state, action) => {
        state.loading       = false;
        state.currentDriver = action.payload;
        // sync into allDrivers
        const ai = state.allDrivers.findIndex((d) => d.listingId === action.payload.listingId);
        if (ai >= 0) state.allDrivers[ai] = action.payload; else state.allDrivers.push(action.payload);
        // sync into listings
        const li = state.listings.findIndex((d) => d.listingId === action.payload.listingId);
        if (li >= 0) state.listings[li] = action.payload; else state.listings.push(action.payload);
      })
      .addCase(upsertDriver.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? "Failed";
      });

    builder.addCase(upsertDriverPhotos.fulfilled, (state, action) => {
      if (state.currentDriver) state.currentDriver.photos = action.payload;
    });

    builder.addCase(deleteDriver.fulfilled, (state, action) => {
      state.allDrivers = state.allDrivers.filter((d) => d.listingId !== action.payload);
      state.listings   = state.listings.filter((d)   => d.listingId !== action.payload);
      if (state.currentDriver?.listingId === action.payload) state.currentDriver = null;
    });

builder.addCase(setActiveVehicle.fulfilled, (state, action) => {
      const { hostId, listingId, userId, isSelected } = action.payload;

      // Mirrors the thunk exactly: only touch the tapped listing, plus any
      // OTHER listing that had driverId === this same userId. Listings
      // driven by a different driver stay untouched.
      const applyUpdate = (d: DriverProfile): DriverProfile => {
        if (d.listingId === listingId) {
          return { ...d, isSelected, driverId: isSelected ? userId : null };
        }
        if (d.hostId === hostId && d.driverId === userId && d.isSelected) {
          return { ...d, isSelected: false, driverId: null };
        }
        return d;
      };

      state.listings   = state.listings.map(applyUpdate);
      state.allDrivers = state.allDrivers.map(applyUpdate);

      if (state.currentDriver) {
        state.currentDriver = state.listings.find((d) => d.listingId === state.currentDriver!.listingId)
          ?? state.currentDriver;
      }
    });

    builder.addCase(fetchUserTrips.fulfilled,        () => {});
    builder.addCase(fetchOperatorsByUserId.fulfilled, () => {});
  },
});

export default fleetSlice.reducer;

export const {
  setCurrentDriver,
  setListings,
  setAllDrivers,
  setUnsubscribe,
  setActiveTrip,
  updateActiveTripFields,
  setActiveParcel,
  setPendingTripRequest,
  clearPendingTripRequest,
  setPendingParcelRequest,
  clearPendingParcelRequest,
  setError,
  setLoading,
  clearActiveTrip,
  clearAll,
} = fleetSlice.actions;

export const selectCurrentDriver        = (s: RootState) => s.fleet.currentDriver;
export const selectListings             = (s: RootState) => s.fleet.listings;
export const selectAllDrivers           = (s: RootState) => s.fleet.allDrivers;
export const selectActiveTrip           = (s: RootState) => s.fleet.activeTrip;
export const selectActiveParcel         = (s: RootState) => s.fleet.activeParcel;
export const selectPendingTripRequest   = (s: RootState) => s.fleet.pendingTripRequest;
export const selectPendingParcelRequest = (s: RootState) => s.fleet.pendingParcelRequest;
export const selectDriversLoading       = (s: RootState) => s.fleet.loading;
export const selectDriversError         = (s: RootState) => s.fleet.error;
export const selectOperators            = (s: RootState) => s.fleet.allDrivers;
export const selectOperatorLoading      = (s: RootState) => s.fleet.loading;